import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import {
  cn,
  Icon,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@kognitos/lattice';

import type { ISidebarNavItem } from './types';
import SidebarRowContent from './SidebarRowContent';

interface ISidebarNavItemProps {
  item: ISidebarNavItem;
  isCollapsed: boolean;
  url: string;
  /** Resolved `item.activeUnder` sections that also highlight this row. */
  activeUrls?: readonly string[];
}

/**
 * A single navigating sidebar row.
 *
 * @param {ISidebarNavItemProps} props - Component props
 * @param {ISidebarNavItem} props.item - The nav entry to render
 * @param {boolean} props.isCollapsed - Whether the sidebar is collapsed to icons
 * @param {string} props.url - Resolved destination for the entry
 * @param {string[]} props.activeUrls - Other sections that highlight the entry
 * @returns {JSX.Element} The sidebar row
 */
export function SidebarNavItem({
  item,
  isCollapsed,
  url,
  activeUrls = [],
}: ISidebarNavItemProps) {
  const { t } = useTranslation(item.ns);
  const { pathname } = useLocation();
  const isActiveUnderOwnedSection = activeUrls.some(
    activeUrl => pathname === activeUrl || pathname.startsWith(`${activeUrl}/`),
  );
  const title = t(item.titleKey);

  return (
    <SidebarMenuItem data-tour-id={`sidebar-${item.key}-tab`}>
      <SidebarMenuButton className="app-sidebar-row" tooltip={title} asChild>
        <div>
          <NavLink
            to={url}
            aria-label={isCollapsed ? title : undefined}
            className={({ isActive }) =>
              cn(
                'flex h-full w-full items-center',
                (isActive || isActiveUnderOwnedSection) && 'active',
              )
            }
          >
            <SidebarRowContent
              icon={<Icon type={item.icon} size={16} />}
              label={title}
              isCollapsed={isCollapsed}
            />
          </NavLink>
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export default SidebarNavItem;
